import classNames from "classnames";
import { forwardRef, HTMLAttributes, KeyboardEvent, ReactNode, useEffect, useState } from "react";
import "./kuika-avatar.scss";

export interface IKuikaAvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Diameter in px. Drives width, height and the default glyph size. */
  size?: number;
  /** Photo. Falls back to `children` when absent or when the request fails. */
  src?: string;
  /** What is drawn without a photo — initials, almost always. */
  children?: ReactNode;
  alt?: string;
  /**
   * Who this is, for the colour of the photoless circle. Build it with `getIdentitySeed`
   * so that every surface keys the same person the same way. Omit it and the circle stays
   * the neutral grey, which is what an unknown person should look like.
   */
  seed?: string | null;
  /**
   * Opt-in keyboard-operable trigger: adds `role="button"`, joins the tab order (`tabIndex={0}`)
   * and shows the focus ring, and Enter/Space dispatch a real click on the circle. Off by default,
   * so a plain identity circle — most avatars — stays a static, non-interactive span.
   *
   * `onClick` is optional: as the child of a `KuikaDropdownMenu` (the account menu's avatar) the
   * menu opens from the dropdown's own trigger wrapper, which the dispatched click bubbles to, so
   * the avatar has nothing to run itself. Pass `onClick` when the avatar opens something directly.
   */
  interactive?: boolean;
}

/** The identity grounds, as token names. See `--avatar-*` in `color-palette.scss`. */
const IDENTITY_COLOUR_COUNT: number = 10;

/** The fields a person arrives with. Different endpoints send different subsets. */
export interface IIdentitySeedParts {
  firstName?: string | null;
  lastName?: string | null;
  /** The address. Some payloads spell it `userName`; pass whichever is present. */
  username?: string | null;
  /** An already-joined display name, for the payloads that send only that. */
  fullName?: string | null;
}

/**
 * The one key a person is coloured by.
 *
 * It has to be something EVERY surface can produce from what it was sent, or the same
 * colleague ends up two colours: an id would be the stabler choice, but several table
 * endpoints send a `createdUser` of nothing but a first and last name, so keying on the id
 * would have coloured those columns differently from the rest of the product — which is
 * the whole thing this is meant to fix.
 *
 * So: the name, and the address only when there is no name. Normalised, because a person
 * who arrives as "Furkan Ceylan" on one screen and "furkan  ceylan" on another is one
 * person and gets one colour. The cost is that a rename re-rolls the colour; the benefit is
 * that every screen agrees, which is the property that was actually asked for.
 */
export function getIdentitySeed(parts: IIdentitySeedParts | null | undefined): string | undefined {
  const name: string = [parts?.firstName ?? "", parts?.lastName ?? ""].join(" ").trim();
  const resolved: string = name || (parts?.fullName ?? "").trim() || (parts?.username ?? "").trim();
  const normalised: string = resolved.toLowerCase().replace(/\s+/g, " ").trim();
  return normalised.length > 0 ? normalised : undefined;
}

/**
 * Which of the ten a seed lands on. FNV-1a because the obvious `hash * 31 + charCode` rolls
 * its low bits far too predictably for a ten-bucket modulo — two addresses on the same
 * domain would keep landing on the same colour.
 */
export function getIdentityColourIndex(seed: string): number {
  // Normalised here too, so a caller that hands over a raw name still lands on the same
  // colour as one that went through `getIdentitySeed`.
  const key: string = seed.toLowerCase().replace(/\s+/g, " ").trim();
  let hash: number = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return (hash % IDENTITY_COLOUR_COUNT) + 1;
}

/**
 * The circle that stands for a person.
 *
 * It replaces antd's `Avatar` on the designer's own surfaces, which mattered for one
 * reason: antd paints its fallback in a light-theme literal, so every identity circle in
 * the header and the profile dialogs stayed pale-grey-on-white while the page behind it
 * followed the theme. The default ground and glyph are tokens here.
 *
 * `size` is published as `--kuika-avatar-size`, so a caller's stylesheet can restate the
 * diameter and the glyph follows it without a second number to keep in sync.
 *
 * Without a photo it takes the person's own colour, one of ten, picked from `seed`. That
 * used to be a per-surface decision: the profile dialog hashed the display name into a
 * continuous `oklch()` hue, the header and the team table painted every circle the same
 * light-theme grey, and nobody else agreed either — so the same colleague was a different
 * colour on every screen, and changed colour when they changed their name.
 *
 * It forwards its ref and spreads the rest of its props, because it is used as a trigger:
 * `KuikaDropdownMenu` injects handlers and `aria-*` onto whatever child it is given, and an
 * avatar that swallowed them would silently stop opening the menu. `interactive` adds the
 * keyboard half — `role="button"`, `tabIndex={0}`, Enter/Space, the focus ring — that injected
 * props alone would not; Enter/Space dispatch a click, so they open whatever a pointer click
 * opens: the dropdown's trigger wrapper, or the caller's own `onClick`.
 */
export const KuikaAvatar = forwardRef<HTMLSpanElement, IKuikaAvatarProps>(
  (
    {
      size = 32,
      src,
      children,
      alt,
      seed,
      interactive = false,
      className,
      style,
      onClick,
      onKeyDown,
      ...rest
    }: IKuikaAvatarProps,
    ref
  ) => {
    const [hasImageFailed, setHasImageFailed] = useState<boolean>(false);

    // A new photo deserves its own attempt — otherwise one broken url would pin the
    // circle to its initials for the rest of the session.
    useEffect((): void => {
      setHasImageFailed(false);
    }, [src]);

    const showsPhoto: boolean = Boolean(src) && !hasImageFailed;
    // A photoless circle is coloured by who it is, never by where it is drawn.
    const identityColour: string | undefined =
      !showsPhoto && seed ? `var(--avatar-${getIdentityColourIndex(seed)})` : undefined;

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
      onKeyDown?.(event);
      if (event.defaultPrevented || (event.key !== "Enter" && event.key !== " ")) {
        return;
      }
      // Space would otherwise scroll the page the avatar sits in. Dispatched as a real click, not
      // a call to `onClick`, so it bubbles to a dropdown's trigger wrapper and `onClick` (when
      // given) receives a genuine MouseEvent.
      event.preventDefault();
      event.currentTarget.click();
    };

    const interactiveProps: Partial<HTMLAttributes<HTMLSpanElement>> = interactive
      ? { role: "button", tabIndex: 0 }
      : {};

    return (
      <span
        {...rest}
        {...interactiveProps}
        ref={ref}
        className={classNames("kuika-avatar", { "kuika-avatar--identity": Boolean(identityColour) }, className)}
        style={{
          ["--kuika-avatar-size" as string]: `${size}px`,
          ["--kuika-avatar-ground" as string]: identityColour,
          ...style
        }}
        onClick={onClick}
        onKeyDown={interactive ? handleKeyDown : onKeyDown}
      >
        {showsPhoto ? (
          <img
            className="kuika-avatar__image"
            src={src}
            alt={alt ?? ""}
            onError={(): void => setHasImageFailed(true)}
          />
        ) : (
          <span className="kuika-avatar__label">{children}</span>
        )}
      </span>
    );
  }
);

KuikaAvatar.displayName = "KuikaAvatar";

export default KuikaAvatar;
